'use client';

import { FileText, Image as ImageIcon, Music, Search, Video } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { AisLink as Link, useAisRuDomen } from '@/components/aisearch/ais-link';
import {
  MIN_DLINA,
  podobratInstrumenty,
  type Instrument,
  type PodskazkaRaboty,
} from '@/lib/poisk-podskazki';
import { getPathname } from '@/i18n/routing';
import { CATALOG_QUERY_MAX, catalogQuery } from '@/lib/shared-catalog-url';
import { SHARED_PREFIX } from '@/lib/shared-url';

/**
 * Поиск по готовым работам: одна форма для шапки и для страницы.
 *
 * На широком экране форма стоит в шапке (только в разделе /shared), на
 * узком в шапке места нет, и та же форма стоит вверху страницы над
 * рубриками. Логика у обеих одна: обычная форма GET на корень раздела
 * с параметром `q`. Работает без скриптов, адрес выдачи можно
 * скопировать, «Назад» возвращает к прежнему запросу.
 *
 * Действие формы — всегда корень раздела, а не текущий адрес: со
 * страницы рубрики или файла поиск ведёт в общую выдачу (поиск живёт
 * только на корне, рубрика параметр `q` не читает).
 *
 * Со скриптами отправка перехватывается и идёт переходом внутри сайта:
 * обычная отправка формы перезагружала страницу, и фоновый плеер
 * замолкал на каждом поиске (замечание владельца 26.10.2026). Без
 * скриптов форма работает как прежде.
 */
export function SharedSearchForm({
  query,
  vShapke = false,
  className = '',
  instrumenty,
  krupno = false,
  avtofokus = false,
  onPereshod,
}: {
  /** Текущий запрос, чтобы он остался в поле. */
  query: string;
  /** Вид для шапки: поле и кнопка одной рамкой, с подсказками. */
  vShapke?: boolean;
  className?: string;
  /** Инструменты сайта для подсказок (только в шапке). */
  instrumenty?: readonly Instrument[];
  /** Крупнее, для меню на телефоне: 16 px, иначе iOS увеличивает страницу. */
  krupno?: boolean;
  avtofokus?: boolean;
  /** Человек ушёл по поиску или подсказке: меню можно закрыть. */
  onPereshod?: () => void;
}) {
  const t = useTranslations('ais_layout');
  const locale = useLocale();
  const ruDomen = useAisRuDomen();
  // На русском домене язык в адресе не пишется (см. ais-link.tsx).
  const action = ruDomen ? SHARED_PREFIX : getPathname({ href: SHARED_PREFIX, locale });
  const router = useRouter();

  const otpravit = (e: FormEvent<HTMLFormElement>) => {
    const q = new FormData(e.currentTarget).get('q');
    if (typeof q !== 'string' || q.trim() === '') return;
    e.preventDefault();
    router.push(`${action}?${new URLSearchParams({ q: q.trim() }).toString()}`);
  };

  if (vShapke) {
    return (
      <FormaVShapke
        query={query}
        action={action}
        className={className}
        instrumenty={instrumenty}
        krupno={krupno}
        avtofokus={avtofokus}
        onPereshod={onPereshod}
      />
    );
  }

  return (
    <form
      role="search"
      method="get"
      action={action}
      onSubmit={otpravit}
      className={`flex gap-2 ${className}`}
    >
      <label className="relative min-w-0 flex-1">
        <span className="sr-only">{t('shared_search_label')}</span>
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]"
        />
        <input
          // key: при переходе на другой запрос поле обновляется, а не
          // держит прежний текст из первой отрисовки.
          key={query}
          type="search"
          name="q"
          defaultValue={query}
          placeholder={t('shared_search_placeholder')}
          maxLength={CATALOG_QUERY_MAX}
          required
          enterKeyHint="search"
          className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-elevated)] py-2.5 pl-9 pr-3 text-base outline-none focus:border-[var(--color-brand-500)]"
        />
      </label>
      <button
        type="submit"
        className="shrink-0 rounded-xl bg-[var(--color-brand-solid,var(--color-brand-500))] px-5 py-2.5 font-medium text-[var(--color-on-brand)] hover:opacity-90"
      >
        {t('shared_search_button')}
      </button>
    </form>
  );
}

/**
 * Форма в шапке: запрос берётся из адреса. Отдельный компонент, потому
 * что `useSearchParams` требует границы Suspense, а шапка общая для
 * всех страниц витрины, в том числе статических.
 */
export function SharedSearchFormIzAdresa(props: {
  className?: string;
  instrumenty?: readonly Instrument[];
  krupno?: boolean;
  avtofokus?: boolean;
  onPereshod?: () => void;
}) {
  const params = useSearchParams();
  return <SharedSearchForm query={catalogQuery(params)} vShapke {...props} />;
}

/** Значок работы без обложки — по виду содержимого. */
function ZnachokVida({ vid }: { vid: string }) {
  const Ikonka =
    vid === 'audio' ? Music : vid === 'video' ? Video : vid === 'image' ? ImageIcon : FileText;
  return <Ikonka aria-hidden className="h-4 w-4 text-[var(--color-text-muted)]" />;
}

/**
 * Поле поиска в шапке с подсказками (просьба владельца 29.10.2026).
 *
 * Поле и кнопка — одно целое: общая рамка, кнопка внутри справа и только
 * когда есть что искать. Пока человек печатает, ниже всплывают
 * подходящие инструменты сайта и до пяти готовых работ витрины
 * (/api/shared/podskazki, с задержкой, чтобы не спрашивать API на
 * каждую букву). Стрелки выбирают, Enter открывает выбранное, а без
 * выбора — обычную выдачу; Escape и щелчок мимо закрывают.
 */
function FormaVShapke({
  query,
  action,
  className,
  instrumenty,
  krupno,
  avtofokus,
  onPereshod,
}: {
  query: string;
  action: string;
  className: string;
  instrumenty?: readonly Instrument[];
  krupno: boolean;
  avtofokus: boolean;
  onPereshod?: () => void;
}) {
  const t = useTranslations('ais_layout');
  const router = useRouter();
  const id = useId();
  const korobka = useRef<HTMLFormElement>(null);
  const spisok = useRef<HTMLUListElement>(null);
  const [tekst, setTekst] = useState(query);
  const [otkryto, setOtkryto] = useState(false);
  const [raboty, setRaboty] = useState<PodskazkaRaboty[]>([]);
  const [ishchem, setIshchem] = useState(false);
  const [aktivnyj, setAktivnyj] = useState(-1);

  const q = tekst.trim();
  const dlinnyj = q.length >= MIN_DLINA;
  const instr = dlinnyj ? podobratInstrumenty(q, instrumenty ?? []) : [];

  // Работы витрины по запросу: с задержкой и с отменой прежнего запроса.
  useEffect(() => {
    if (!otkryto || !dlinnyj) {
      setRaboty([]);
      return;
    }
    const otmena = new AbortController();
    const tajmer = setTimeout(() => {
      setIshchem(true);
      fetch(`/api/shared/podskazki?${new URLSearchParams({ q }).toString()}`, {
        signal: otmena.signal,
      })
        .then((r) =>
          r.ok ? (r.json() as Promise<{ raboty?: PodskazkaRaboty[] }>) : { raboty: [] },
        )
        .then((d) => {
          setRaboty(d.raboty ?? []);
          setIshchem(false);
        })
        .catch(() => {
          if (!otmena.signal.aborted) setIshchem(false);
        });
    }, 250);
    return () => {
      clearTimeout(tajmer);
      otmena.abort();
    };
  }, [q, dlinnyj, otkryto]);

  // Щелчок мимо формы закрывает подсказки.
  useEffect(() => {
    if (!otkryto) return;
    const mimo = (e: PointerEvent) => {
      if (!korobka.current?.contains(e.target as Node)) setOtkryto(false);
    };
    document.addEventListener('pointerdown', mimo);
    return () => {
      document.removeEventListener('pointerdown', mimo);
    };
  }, [otkryto]);

  const ushli = () => {
    setOtkryto(false);
    setAktivnyj(-1);
    onPereshod?.();
  };

  const otpravit = (e: FormEvent<HTMLFormElement>) => {
    if (q === '') return;
    e.preventDefault();
    // Выбрана подсказка стрелками — открываем её, а не выдачу.
    const vybrano = aktivnyj >= 0 ? spisok.current?.querySelectorAll('a')[aktivnyj] : undefined;
    if (vybrano) {
      vybrano.click();
      return;
    }
    ushli();
    router.push(`${action}?${new URLSearchParams({ q }).toString()}`);
  };

  const vsego = instr.length + raboty.length;
  const strelki = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOtkryto(false);
      setAktivnyj(-1);
      return;
    }
    if (vsego === 0 || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return;
    e.preventDefault();
    setOtkryto(true);
    setAktivnyj((a) => (e.key === 'ArrowDown' ? (a + 1) % vsego : a <= 0 ? vsego - 1 : a - 1));
  };

  const pokazat = otkryto && dlinnyj && (vsego > 0 || ishchem);
  const punkt = (i: number) =>
    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-[var(--color-bg-elevated)] ${
      i === aktivnyj ? 'bg-[var(--color-bg-elevated)]' : ''
    }`;
  const zagolovok =
    'px-3 pb-1 pt-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]';

  return (
    <form
      ref={korobka}
      role="search"
      method="get"
      action={action}
      onSubmit={otpravit}
      className={`relative flex items-center rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-elevated)] focus-within:border-[var(--color-brand-500)] ${
        krupno ? 'h-11' : 'h-9'
      } ${className}`}
    >
      <label className="relative flex h-full min-w-0 flex-1 items-center">
        <span className="sr-only">{t('shared_search_label')}</span>
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 h-4 w-4 text-[var(--color-text-muted)]"
        />
        <input
          key={query}
          type="search"
          name="q"
          defaultValue={query}
          autoFocus={avtofokus}
          role="combobox"
          aria-expanded={pokazat}
          aria-controls={`${id}-spisok`}
          aria-autocomplete="list"
          aria-activedescendant={aktivnyj >= 0 ? `${id}-${String(aktivnyj)}` : undefined}
          autoComplete="off"
          onInput={(e) => {
            setTekst(e.currentTarget.value);
            setOtkryto(true);
            setAktivnyj(-1);
          }}
          onFocus={() => {
            setOtkryto(true);
          }}
          onKeyDown={strelki}
          placeholder={t('shared_search_placeholder')}
          maxLength={CATALOG_QUERY_MAX}
          required
          enterKeyHint="search"
          className={`h-full w-full bg-transparent pl-9 pr-2 outline-none ${krupno ? 'text-base' : 'text-sm'}`}
        />
      </label>
      {q !== '' && (
        <button
          type="submit"
          aria-label={t('shared_search_button')}
          className={`mr-1 inline-flex shrink-0 items-center justify-center rounded-lg bg-[var(--color-brand-solid,var(--color-brand-500))] text-[var(--color-on-brand)] hover:opacity-90 ${
            krupno ? 'h-9 w-9' : 'h-7 w-7'
          }`}
        >
          <Search aria-hidden className="h-3.5 w-3.5" />
        </button>
      )}

      {pokazat && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[70vh] overflow-y-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-1.5 shadow-2xl">
          <ul ref={spisok} id={`${id}-spisok`} role="listbox" aria-label={t('shared_search_label')}>
            {instr.length > 0 && (
              <li role="presentation" className={zagolovok}>
                {t('podskazki_instrumenty')}
              </li>
            )}
            {instr.map((i, n) => (
              <li
                key={i.href}
                role="option"
                id={`${id}-${String(n)}`}
                aria-selected={n === aktivnyj}
              >
                <Link href={i.href} onClick={ushli} className={punkt(n)}>
                  <Search aria-hidden className="h-4 w-4 text-[var(--color-brand-500)]" />
                  <span className="truncate">{i.name}</span>
                </Link>
              </li>
            ))}
            {raboty.length > 0 && (
              <li role="presentation" className={zagolovok}>
                {t('podskazki_raboty')}
              </li>
            )}
            {raboty.map((r, k) => {
              const n = instr.length + k;
              return (
                <li
                  key={r.id}
                  role="option"
                  id={`${id}-${String(n)}`}
                  aria-selected={n === aktivnyj}
                >
                  <Link href={r.href} onClick={ushli} className={punkt(n)}>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md bg-[var(--color-bg-elevated)]">
                      {r.oblozhka ? (
                        <img
                          src={r.oblozhka}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <ZnachokVida vid={r.vid} />
                      )}
                    </span>
                    <span className="line-clamp-2 leading-snug">{r.title}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
          {ishchem && raboty.length === 0 && (
            <p className="px-3 py-2 text-sm text-[var(--color-text-muted)]">
              {t('podskazki_ishchem')}
            </p>
          )}
          <button
            type="submit"
            className="mt-1 w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-[var(--color-brand-500)] hover:bg-[var(--color-bg-elevated)]"
          >
            {t('podskazki_vse', { q })}
          </button>
        </div>
      )}
    </form>
  );
}
